import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../../components/header/header.component';
import { AgGridListComponent } from '../../components/ag-grid-list/ag-grid-list.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { GlobalService } from '../../services/global.service';
import { DropdownService } from '../../services/dropdown.service';
import { ExceptionService } from '../../services/exception.service';
import { FormService } from '../../services/form.service';

@Component({
  selector: 'app-exception-list-page',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, AgGridListComponent, SelectInputComponent, SearchInputComponent],
  templateUrl: './exception-list-page.component.html',
  styleUrl: './exception-list-page.component.css',
})
export class ExceptionListPageComponent {

  @ViewChild('resolve_modal') resolve_modal: any;

  params: any = {};
  items: any = { data: [], count: 0 };
  active_tab = 'New';
  tab_counts: any = {};
  selected_exception_id: number | null = null;

  university_options: any[] = [];
  filtered_academic_years: any[] = [];
  filtered_exams: any[] = [];
  filtered_subjects: any[] = [];

  // ── Permission helpers ────────────────────────────────────────────────────
  // "Assigned Exceptions" page reuses this component: any user (any role) landing here
  // should only ever see exceptions assigned to themselves.
  get is_assigned_exceptions_page(): boolean {
    return this.router.url.split('?')[0] === '/assigned-exceptions/list';
  }

  get page_name(): string {
    return this.is_assigned_exceptions_page ? 'Assigned Exceptions' : 'Exceptions';
  }

  get is_view_only(): boolean {
    const role_name: string = (this.gs.user_role_data?.name || '').toLowerCase();
    return role_name.includes('evaluator');
  }

  get is_super_admin(): boolean {
    return this.gs.access_scope.is_all;
  }

  get is_admin(): boolean {
    return !!this.gs.user_role_data?.exceptions_permission;
  }

  get is_assigned_user(): boolean {
    return !this.is_view_only && !this.is_admin;
  }

  // ── Tabs ──────────────────────────────────────────────────────────────────
  get tabs() {
    const all_tabs = [{ label: 'NEW', value: 'New' }, { label: 'REPLIED', value: 'Replied' }, { label: 'ASSIGNED', value: 'Assigned' }];
    return this.is_assigned_exceptions_page ? all_tabs.filter(t => t.value !== 'New') : all_tabs;
  }

  // ── Status badge renderer ─────────────────────────────────────────────────
  status_badge(status: string): string {
    const map: any = {
      New: { label: 'Pending', color: '#3F41D1', bg: '#EBF2FF' },
      Replied: { label: 'Replied', color: '#B45309', bg: '#FDF4E7' },
      Assigned: { label: 'Pending', color: '#3F41D1', bg: '#EBF2FF' },
      Rejected: { label: 'Rejected', color: '#9B1C1C', bg: '#FFE8E8' },
      Resolved: { label: 'Resolved', color: '#15803D', bg: '#E8F5EE' },
    };
    const s = map[status] || { label: status, color: '#6B7280', bg: '#F3F4F6' };
    return `<div style="display:inline-flex;align-items:center;gap:8px;background:${s.bg};color:${s.color};border-radius:128px;padding:8px 16px;width:125px;height:32px;box-sizing:border-box;font-size:14px;font-weight:600;white-space:nowrap">
      <span style="width:8px;height:8px;border-radius:50%;background:${s.color};flex-shrink:0"></span>${s.label}
    </div>`;
  }

  // ── Admin columns ─────────────────────────────────────────────────────────
  admin_columns: any = [
    {
      headerName: 'Faculty Name', flex: 2, minWidth: 160,
      cellRenderer: (p: any) => {
        const name = p.data?.User?.name || '-';
        const role = p.data?.User?.Role?.name || 'Evaluator';
        return `<div style="display:flex;flex-direction:column;justify-content:center;height:100%;gap:1px">
          <span style="font-size:14px;font-weight:500;color:#212121;letter-spacing:0.02em;line-height:1.2">${name}</span>
          <span style="font-size:12px;font-weight:400;color:#6b7280;letter-spacing:0.02em;line-height:1.2">${role}</span>
        </div>`;
      }
    },
    {
      headerName: 'Barcode', flex: 1.2, minWidth: 120,
      cellRenderer: (p: any) => {
        const barcode = p.data?.Script?.barcode || p.data?.meta_data?.script?.barcode || '-';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:400;color:#212121;letter-spacing:0.02em">${barcode}</div>`;
      }
    },
    {
      headerName: 'Contact', flex: 1.8, minWidth: 160,
      cellRenderer: (p: any) => {
        const contact = p.data?.User?.contact || '';
        const email = p.data?.User?.email || '';
        return `<div style="display:flex;flex-direction:column;justify-content:center;height:100%;gap:1px">
          <span style="font-size:14px;font-weight:500;color:#212121;letter-spacing:0.02em;line-height:1.2">${contact ? '+91' + contact : '-'}</span>
          <span style="font-size:12px;font-weight:400;color:#6b7280;letter-spacing:0.02em;line-height:1.2">${email || '-'}</span>
        </div>`;
      }
    },
    {
      headerName: 'Exception', flex: 1.5, minWidth: 140,
      cellRenderer: (p: any) => {
        const exception = Array.isArray(p.data?.exception) ? p.data.exception.join(', ') : (p.data?.exception || '-');
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:400;color:#212121;letter-spacing:0.02em">${exception}</div>`;
      }
    },
    {
      headerName: 'Description', flex: 2.5, minWidth: 200,
      cellRenderer: (p: any) => {
        const desc = p.data?.description || 'NA';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:400;color:#212121;letter-spacing:0.02em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${desc}">${desc}</div>`;
      }
    },
    {
      headerName: 'Action', flex: 0.8, minWidth: 80,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: (p: any) => this.edit_btn(p.data?.id),
      onCellClicked: (p: any) => { if (p.data?.id) this.view_exception(p.data.id, p.data?.meta_data?.script?.id); }
    }
  ];

  // ── Evaluator columns ─────────────────────────────────────────────────────
  evaluator_columns: any[] = [
    {
      headerName: 'Script ID', flex: 1.2, minWidth: 120,
      cellRenderer: (p: any) => {
        const sid = p.data?.meta_data?.script?.script_id || p.data?.Script?.script_id || '-';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:400;color:#212121">${sid}</div>`;
      }
    },
    {
      headerName: 'Exception', flex: 1.5, minWidth: 140,
      cellRenderer: (p: any) => {
        const exception = Array.isArray(p.data?.exception) ? p.data.exception.join(', ') : (p.data?.exception || '-');
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:400;color:#212121">${exception}</div>`;
      }
    },
    {
      headerName: 'Description', flex: 2, minWidth: 160,
      cellRenderer: (p: any) => {
        const desc = p.data?.description || 'NA';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:400;color:#212121;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${desc}">${desc}</div>`;
      }
    },
    {
      headerName: 'Admin Comments', flex: 2, minWidth: 160,
      cellRenderer: (p: any) => {
        const comment = p.data?.comment || 'NA';
        return `<div style="display:flex;align-items:center;height:100%;font-size:14px;font-weight:400;color:#212121;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${comment}">${comment}</div>`;
      }
    },
    {
      headerName: 'Status', flex: 1.2, minWidth: 140,
      cellStyle: { display: 'flex', alignItems: 'center' },
      cellRenderer: (p: any) => this.status_badge(p.data?.status || 'New'),
    },
    {
      headerName: 'Action', flex: 0.8, minWidth: 80,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      cellRenderer: (p: any) => {
        const status = p.data?.status;
        const edit_btn = status === 'Replied'
          ? `<button data-action="reply" title="Edit / Reply" style="background:#FEF3E7;border:none;border-radius:50%;cursor:pointer;width:36px;height:36px;display:flex;align-items:center;justify-content:center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B45309" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>` : '';
        return `<div style="display:flex;align-items:center;justify-content:center;height:100%;gap:6px">${this.view_btn(p.data?.id)}${edit_btn}</div>`;
      },
      onCellClicked: (p: any) => {
        if (p.data?.id) this.view_exception(p.data.id, p.data?.meta_data?.script?.id);
      }
    },
  ];

  get columns(): any[] {
    return this.is_view_only ? this.evaluator_columns : this.admin_columns;
  }

  // ── Bulk download columns ─────────────────────────────────────────────────
  get bulk_columns(): any[] {
    return !this.is_admin
      ? [
        { column_name: 'Script ID', type: 'text', return_as: 'Script.script_id' },
        { column_name: 'Exception', type: 'text', return_as: 'exception' },
        { column_name: 'Description', type: 'text', return_as: 'description' },
        { column_name: 'Admin Comments', type: 'text', return_as: 'comment' },
        { column_name: 'Status', type: 'text', return_as: 'status' },
      ]
      : [
        { column_name: 'Faculty Name', type: 'text', return_as: 'User.name' },
        { column_name: 'Contact', type: 'text', return_as: 'User.contact' },
        { column_name: 'Email', type: 'text', return_as: 'User.email' },
        { column_name: 'Barcode', type: 'text', return_as: 'Script.barcode' },
        { column_name: 'Exception', type: 'text', return_as: 'exception' },
        { column_name: 'Description', type: 'text', return_as: 'description' },
        { column_name: 'Status', type: 'text', return_as: 'status' },
      ];
  }

  // ── Button helpers ────────────────────────────────────────────────────────
  edit_btn(id: any) {
    return `<div style="display:flex;align-items:center;justify-content:center;height:100%">
      <button data-id="${id}" class="view-btn" style="background:#EAEBFF;border:none;border-radius:50%;cursor:pointer;width:36px;height:36px;display:flex;align-items:center;justify-content:center">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3F41D1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
        </svg>
      </button>
    </div>`;
  }

  view_btn(id: any) {
    return `<div style="display:flex;align-items:center;justify-content:center;height:100%">
      <button data-id="${id}" class="view-btn" style="background:#EAEBFF;border:none;border-radius:50%;cursor:pointer;width:36px;height:36px;display:flex;align-items:center;justify-content:center">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3F41D1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"></path>
          <circle cx="12" cy="12" r="3"></circle>
        </svg>
      </button>
    </div>`;
  }

  constructor(
    public gs: GlobalService,
    public dds: DropdownService,
    public es: ExceptionService,
    public router: Router,
    public fs: FormService,
    private route: ActivatedRoute,
    public ar: ActivatedRoute
  ) { }

  async ngOnInit() {
    this.ar.queryParams.subscribe(async params => {
      this.params = { ...params }
      // Access Level: lock the filters this user cannot change before the first fetch.
      this.gs.apply_access_scope_to_params(this.params);
      this.active_tab = this.params.status || (this.is_assigned_exceptions_page ? 'Assigned' : 'New');
    })
    await this.dds.get_dropdown_list(['assessment_data']);
    this.university_options = this.dds.dropdown_list?.assessment_university_list || [];

    const qp = this.route.snapshot.queryParams;
    const from_notification = qp['university_id'] || qp['academic_year_id'] || qp['exam_id'] || qp['subject_id'];

    if (from_notification) {
      if (!this.is_super_admin) {
        this.params.university_id = this.gs.user_data?.university_id || null;
      } else {
        this.params.university_id = qp['university_id'] ? +qp['university_id'] : null;
      }
      this.params.academic_year_id = qp['academic_year_id'] ? +qp['academic_year_id'] : null;
      this.params.exam_id = qp['exam_id'] ? +qp['exam_id'] : null;
      this.params.subject_id = qp['subject_id'] ? +qp['subject_id'] : null;
    } else {
      if (!this.is_super_admin) {
        this.params.university_id = this.gs.user_data?.university_id || null;
      }
      this.patch_latest_assessment();
    }

    this.update_filtered_options();
    await this.get_list();
  }

  patch_latest_assessment() {
    const all_assessments: any[] = this.dds.dropdown_list?.assessment_data_list || [];
    if (!all_assessments.length) return;
    const latest = all_assessments.reduce((prev: any, curr: any) => (curr.id > prev.id ? curr : prev));
    if (this.is_super_admin) {
      this.params.university_id = latest.university_id || null;
    }
    this.params.academic_year_id = latest.academic_year_id || null;
    this.params.exam_id = latest.exam_id || null;
    this.params.subject_id = latest.subject_id || null;
  }

  update_filtered_options() {
    const source: any[] = this.dds.dropdown_list?.assessment_data_list || [];
    const has_uni = !!this.params.university_id;
    const has_ay = !!this.params.academic_year_id;
    const has_exam = !!this.params.exam_id;

    // Academic Year: filter by university
    const _ay_seen = new Set();
    this.filtered_academic_years = source
      .filter((a: any) => !has_uni || a.university_id == this.params.university_id)
      .reduce((acc: any[], a: any) => {
        const id = a.AcademicYear?.id;
        if (id && !_ay_seen.has(id)) { _ay_seen.add(id); acc.push({ id, title: a.AcademicYear?.name || 'none', value: id }); }
        return acc;
      }, []);

    // Exam: filter by university AND academic year
    const _exam_seen = new Set();
    this.filtered_exams = source
      .filter((a: any) =>
        (!has_uni || a.university_id == this.params.university_id) &&
        (!has_ay || a.academic_year_id == this.params.academic_year_id)
      )
      .reduce((acc: any[], a: any) => {
        const id = a.Exam?.id;
        if (id && !_exam_seen.has(id)) {
          _exam_seen.add(id);
          acc.push({ id, title: (a.Exam?.name || 'none') + (a.Exam?.code ? ' (' + a.Exam.code + ')' : ''), value: id });
        }
        return acc;
      }, []);

    // Subject: filter by university AND academic year AND exam
    const _sub_seen = new Set();
    this.filtered_subjects = source
      .filter((a: any) =>
        (!has_uni || a.university_id == this.params.university_id) &&
        (!has_ay || a.academic_year_id == this.params.academic_year_id) &&
        (!has_exam || a.exam_id == this.params.exam_id)
      )
      .reduce((acc: any[], a: any) => {
        const id = a.Subject?.id;
        if (id && !_sub_seen.has(id)) {
          _sub_seen.add(id);
          acc.push({ id, title: (a.Subject?.name || 'none') + (a.Subject?.code ? ' (' + a.Subject.code + ')' : ''), value: id });
        }
        return acc;
      }, []);
  }

  on_university_change() {
    this.params.academic_year_id = null;
    this.params.exam_id = null;
    this.params.subject_id = null;
    this.update_filtered_options();
    this.apply_filters();
  }

  on_academic_year_change() {
    this.params.exam_id = null;
    this.params.subject_id = null;
    this.update_filtered_options();
    this.apply_filters();
  }

  on_exam_change() {
    this.params.subject_id = null;
    this.update_filtered_options();
    this.apply_filters();
  }

  async get_list(extra?: any) {
    try {
      const user_id = this.gs.user_data?.id;
      let filters: any = { ...this.params, ...extra };

      if (this.is_assigned_exceptions_page) {
        filters.assigned_to_me = true;
        filters.status = this.active_tab;
      } else if (this.is_view_only) {
        filters.user_id = user_id;
      } else {
        filters.status = this.active_tab;
        if (this.is_assigned_user) filters.assigned_user_id = user_id;
      }

      const res: any = await this.es.get_list(filters);
      this.items = res;
      if (res?.tab_counts) this.tab_counts = res.tab_counts;
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Failed to fetch exceptions', 'Error', 'error');
    }
  }

  set_tab(tab: string) {
    const path = this.is_assigned_exceptions_page ? '/assigned-exceptions/list' : '/exception/list';
    this.router.navigate([path], { queryParams: { ...this.params, status: tab } });
    this.get_list();
  }

  apply_filters() {
    this.get_list({ page: 1 });
  }

  view_exception(id: number, script_id?: number) {
    const params: any = { id };
    if (script_id) params.script_id = script_id;
    this.router.navigate(['/exception/view'], { queryParams: params });
  }

  async resolve_exception(row: any) {
    if (row?.status === 'Resolved') return;
    try {
      const res: any = await this.es.update_exception({ id: row.id, action: 'mark_as_resolved' });
      if (res?.code === 200) {
        this.gs.toastr_shows_function('Exception marked as resolved', 'Success', 'success');
        await this.get_list();
      } else {
        this.gs.toastr_shows_function(res?.message || 'Failed to resolve', 'Error', 'error');
      }
    } catch (e: any) {
      this.gs.toastr_shows_function(e?.error?.message || 'Something went wrong', 'Error', 'error');
    }
  }

  download() {
    const user_id = this.gs.user_data?.id;
    const extra: any = { download: true };
    if (this.is_assigned_exceptions_page) {
      extra.assigned_to_me = true;
      extra.status = this.active_tab;
    } else if (this.is_view_only) {
      extra.user_id = user_id;
    } else {
      extra.status = this.active_tab;
      if (this.is_assigned_user) extra.assigned_user_id = user_id;
    }
    this.fs.bulk_download(this.es, 'get_list', this.bulk_columns, this.page_name, { ...this.params, ...extra });
  }
}
